import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { Enveloped } from '../../../utils/what-the-api-returns';
/**
 * API may return Either-wrapped response: { value: T }
 *
 * A third copy of the same idea, after core/utils/what-the-api-returns.ts and
 * the back office's own. Left where it is: folding the three together touches
 * every caller and belongs with fixing the 227 routes, not with this pass.
 */
function unwrapResponse<T>(res: { value?: T } | T): T {
  return (res as { value?: T }).value ?? (res as T);
}
import { environment } from '../../../../../environments/environment';
import { MyVatStatus, SelfBillingInvoiceSummary, VatDeclaration } from '../domains/the-vat-status-a-pro-declares';
import {
  OrganizersService as OrganizersSdkService,
  OrganizersControllerGetStripeConnectStatus200Response,
  GetStripeConnectStatusResponse,
  CreateConnectedAccountResponse,
} from '@smatchy/sdk';

export interface ServerApiResponse<T> {
  value?: T;
  /**
   * `unknown`, like the shared `Enveloped` says. The generated DTO describes
   * this field as a string or any object at all, which describes nothing: the
   * two readers of it ask `typeof error === 'string'` and fall back to a
   * message of their own, and that question is legal on unknown.
   */
  error?: unknown;
}

/** Invoice month summary (Story 1.2). */
export interface InvoiceMonthSummary {
  month: number;
  dateRangeStart: string;
  dateRangeEnd: string;
  totalAmountCents: number;
  currency: string;
  status: 'Generated';
}

/** Invoice summaries for year API response (Story 1.2). */
export interface GetInvoiceSummariesForYearResponse {
  year: number;
  months: InvoiceMonthSummary[];
}

/** Invoice details API response (Story 1.3). */
export interface GetInvoiceDetailsResponse {
  organizer: {
    name: string;
    address: { city?: string; zipCode?: string; country?: string };
    country: string;
  };
  billingPeriod: { dateRangeStart: string; dateRangeEnd: string };
  issueDate: string;
  commissionBreakdown: {
    lineItems: Array<{
      event: {
        eventId: string;
        title: string;
        startDate: string;
        endDate?: string;
        costPerParticipant?: number;
        location?: { name?: string };
      };
      stats: {
        organizerEarningCents: number;
        platformEarningCents: number;
        ticketsSold: number;
        eventRevenueCents?: number;
        currency: string;
        commissionPercentage?: number;
      };
    }>;
  };
  totals: {
    totalOrganizerEarningCents: number;
    totalCommissionCents: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class OrganizersService {
  constructor(private readonly _organizersService: OrganizersSdkService, private readonly http: HttpClient) {}

  getStripeConnectStatus(): Observable<ServerApiResponse<GetStripeConnectStatusResponse>> {
    return this._organizersService.getStripeConnectStatus().pipe(
      map((response: OrganizersControllerGetStripeConnectStatus200Response) => ({
        value: response.value,
        error: response.error,
      })),
    );
  }

  createOnboardingLink(): Observable<ServerApiResponse<CreateConnectedAccountResponse>> {
    return this._organizersService.createConnectedAccount().pipe(
      map((response: Enveloped<CreateConnectedAccountResponse> & Partial<CreateConnectedAccountResponse>) => {
        console.log('SDK createConnectedAccount response:', JSON.stringify(response, null, 2));

        // Handle both wrapped response (response.value) and direct response
        if (response.value) {
          // Wrapped response: { error?: string, value?: object }
          return {
            value: response.value,
            error: response.error,
          };
        } else if (response.onboardingUrl) {
          // Direct response: CreateConnectedAccountResponse
          return {
            value: response as CreateConnectedAccountResponse,
            error: undefined,
          };
        } else {
          // Try response as-is (might be error)
          // The whole answer used to be handed back as the error, which is not
          // an error shape at all. The caller only asks whether the error is a
          // string; what it needs to know is that there was one, and the answer
          // stays attached for the console.
          // The whole answer used to be handed back as the error. The reader
          // only asks whether the error is a string; what it needs to know is
          // that there was one, and the answer stays attached for the console.
          return {
            value: undefined,
            error: response.error || { unexpectedAnswer: response },
          };
        }
      }),
    );
  }

  /**
   * Get month summaries for a year (Story 1.2 API).
   * GET /organizers/me/invoices/:year
   */
  getInvoiceSummariesForYear(year: number): Observable<GetInvoiceSummariesForYearResponse> {
    const url = `${environment.api}/organizers/me/invoices/${year}`;
    type Res =
      | GetInvoiceSummariesForYearResponse
      | {
          value: GetInvoiceSummariesForYearResponse;
        };
    return this.http.get<Res>(url).pipe(map(unwrapResponse));
  }

  /**
   * Get full invoice details for a month (Story 1.3 API).
   * GET /organizers/me/invoices/:year/:month/details
   */
  getInvoiceDetails(year: number, month: number): Observable<GetInvoiceDetailsResponse> {
    const url = `${environment.api}/organizers/me/invoices/${year}/${month}/details`;
    type Res = GetInvoiceDetailsResponse | { value: GetInvoiceDetailsResponse };
    return this.http.get<Res>(url).pipe(map(unwrapResponse));
  }

  /**
   * Download invoice PDF for a month (Story 1.8).
   * GET /organizers/me/invoices/:year/:month/pdf
   */
  getInvoicePdfBlob(year: number, month: number): Observable<Blob> {
    const url = `${environment.api}/organizers/me/invoices/${year}/${month}/pdf`;
    return this.http.get(url, { responseType: 'blob' });
  }

  /** GET /organizers/me/vat-status : le statut déclaré, et si les virements l'attendent. */
  getMyVatStatus(): Observable<MyVatStatus> {
    type Res = MyVatStatus | { value: MyVatStatus };
    return this.http.get<Res>(`${environment.api}/organizers/me/vat-status`).pipe(map(unwrapResponse));
  }

  /** PUT /organizers/me/vat-status : déclare le statut et accepte le mandat de facturation. */
  declareMyVatStatus(declaration: VatDeclaration): Observable<MyVatStatus> {
    type Res = MyVatStatus | { value: MyVatStatus };
    return this.http.put<Res>(`${environment.api}/organizers/me/vat-status`, declaration).pipe(map(unwrapResponse));
  }

  /** GET /organizers/me/self-billing-invoices : les factures émises au nom de la ou du pro. */
  getMySelfBillingInvoices(): Observable<SelfBillingInvoiceSummary[]> {
    type Res = { invoices: SelfBillingInvoiceSummary[] } | { value: { invoices: SelfBillingInvoiceSummary[] } };
    return this.http
      .get<Res>(`${environment.api}/organizers/me/self-billing-invoices`)
      .pipe(map((res) => unwrapResponse(res)?.invoices ?? []));
  }

  /** GET /organizers/me/self-billing-invoices/:year/:month/pdf */
  getSelfBillingInvoicePdfBlob(year: number, month: number): Observable<Blob> {
    return this.http.get(`${environment.api}/organizers/me/self-billing-invoices/${year}/${month}/pdf`, {
      responseType: 'blob',
    });
  }
}
